import {
  Camera,
  Check,
  ChevronDown,
  ChevronLeft,
  Dumbbell,
  Flashlight,
  ListOrdered,
  Mail,
  MapPin,
  MessageCircle,
  Plus,
  Timer,
  TrendingUp,
} from "lucide-react";

import { Monogram } from "@/features/portal/components/portal-ui";
import { MealScan } from "@/features/portal/components/meal-scan";
import { photos } from "@/features/portal/content/photos";
import { MUSCLE_LABELS } from "@/features/training/constants/labels";
import { MuscleMap, recoveryTone } from "@/features/training/modules/recovery/components/body-figure";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import { getLocale } from "@/paraglide/runtime.js";
import { Muscle } from "@complete-athlete/enums";

/**
 * The app's screens as the phones on `/features` show them, at an iPhone's 402 pt width and iOS
 * type sizes. Sample people and numbers, nobody's real training. Each screen starts under the
 * Dynamic Island (62 pt) and keeps its point in the top half, where a cropped phone still shows it.
 */

const screen = "absolute inset-0 flex flex-col pt-[62px]";
const well = "bg-foreground/[0.06]";
const roundIcon = cn("grid size-[38px] place-items-center rounded-full", well);

export const kilos = (value: number) =>
  new Intl.NumberFormat(getLocale(), { maximumFractionDigits: 1 }).format(value);

const LargeTitle = ({ kicker, title }: { kicker: string; title: string }) => (
  <div className="px-5 pt-3">
    <p className="text-[15px] font-medium text-muted-foreground">{kicker}</p>
    <h3 className="mt-0.5 text-[34px] leading-[1.1] font-bold tracking-[-0.03em]">{title}</h3>
  </div>
);

/** The home screen: the session with the coach, today's plan and the day's numbers. */
export const DayScreen = () => (
  <div className={screen}>
    <div className="flex items-start justify-between pr-5">
      <LargeTitle kicker={m.features_day_kicker()} title={m.features_day_hello({ name: m.features_sample_athlete() })} />
      <Monogram name={m.features_sample_athlete()} className="mt-5 size-[42px] rounded-full text-[15px]" />
    </div>
    <div className="mx-4 mt-6 rounded-[26px] bg-primary p-5 text-primary-foreground">
      <div className="flex items-center justify-between">
        <span className="rounded-full bg-primary-foreground/12 px-3 py-1 text-[13px] font-semibold">
          {m.features_day_today_at({ time: "18:00" })}
        </span>
        <Timer aria-hidden className="size-5 opacity-70" />
      </div>
      <p className="mt-5 text-[24px] leading-[1.15] font-semibold tracking-[-0.02em]">
        {m.features_day_session({ coach: m.features_sample_coach() })}
      </p>
      <p className="mt-1 flex items-center gap-1.5 text-[15px] opacity-70">
        <MapPin aria-hidden className="size-4" />
        {m.features_sample_studio()}
      </p>
    </div>
    <div className={cn("mx-4 mt-3 rounded-[26px] p-5", well)}>
      <p className="text-[13px] font-semibold tracking-[0.06em] text-muted-foreground uppercase">
        {m.features_day_plan_label()}
      </p>
      <p className="mt-2 text-[22px] font-semibold tracking-[-0.02em]">{m.features_sample_day()}</p>
      <p className="mt-1 text-[15px] text-muted-foreground">{m.features_day_plan_meta({ count: 6, minutes: 55 })}</p>
      <span className="mt-4 flex h-[52px] items-center justify-center rounded-full bg-foreground text-[17px] font-semibold text-background">
        {m.features_day_start()}
      </span>
    </div>
    <div className="mx-4 mt-3 grid grid-cols-2 gap-3">
      <div className={cn("rounded-[22px] p-4", well)}>
        <p className="text-[13px] text-muted-foreground">{m.features_day_kcal()}</p>
        <p className="mt-1 text-[22px] font-semibold tabular-nums">1 240</p>
        <span className="mt-3 block h-[6px] overflow-hidden rounded-full bg-foreground/10">
          <span className="block h-full w-[54%] rounded-full bg-primary" />
        </span>
      </div>
      <div className={cn("rounded-[22px] p-4", well)}>
        <p className="text-[13px] text-muted-foreground">{m.recovery_title()}</p>
        <p className="mt-1 text-[22px] font-semibold tabular-nums">86%</p>
        <span className="mt-3 block h-[6px] overflow-hidden rounded-full bg-foreground/10">
          <span className="block h-full w-[86%] rounded-full bg-primary" />
        </span>
      </div>
    </div>
  </div>
);

type SetState = "done" | "now" | "next";

const SETS: { number: number; weight: number; reps: number; state: SetState }[] = [
  { number: 1, weight: 80, reps: 8, state: "done" },
  { number: 2, weight: 82.5, reps: 8, state: "done" },
  { number: 3, weight: 82.5, reps: 8, state: "now" },
  { number: 4, weight: 82.5, reps: 8, state: "next" },
];

/** The workout player mid-exercise: the movement, the sets with today's weight, one big button. */
export const WorkoutScreen = () => (
  <div className={screen}>
    <div className="flex items-center justify-between px-4 pt-1">
      <span className={roundIcon}>
        <ChevronDown aria-hidden className="size-5" />
      </span>
      <span className="text-center leading-tight">
        <b className="block text-[15px] font-semibold">{m.features_sample_day()}</b>
        <small className="text-[13px] text-muted-foreground tabular-nums">24:18</small>
      </span>
      <span className={roundIcon}>
        <ListOrdered aria-hidden className="size-[18px]" />
      </span>
    </div>
    <div className="relative mx-4 mt-4 aspect-[16/11] overflow-hidden rounded-[28px]">
      <img src={photos.squatMono} alt="" className="size-full object-cover" />
      <span className="absolute top-3 left-3 rounded-full bg-black/55 px-3 py-1.5 text-[13px] font-semibold text-white backdrop-blur-md">
        {m.workout_exercise_of({ index: 2, count: 6 })}
      </span>
    </div>
    <div className="px-5 pt-4">
      <h3 className="text-[26px] leading-tight font-semibold tracking-[-0.02em]">{m.features_ex_squat()}</h3>
      <p className="mt-0.5 text-[15px] text-muted-foreground">
        {MUSCLE_LABELS[Muscle.QUADS]()} · {MUSCLE_LABELS[Muscle.GLUTES]()}
      </p>
    </div>
    <ul className="mt-3 grid gap-2 px-4">
      {SETS.map((set) => (
        <li
          key={set.number}
          className={cn(
            "flex h-[54px] items-center gap-3 rounded-[18px] px-3.5 text-[17px]",
            set.state === "now" ? "bg-primary/12 ring-2 ring-primary ring-inset" : well,
            set.state === "next" && "text-muted-foreground",
          )}
        >
          <span
            className={cn(
              "grid size-[30px] place-items-center rounded-full text-[14px] font-semibold tabular-nums",
              set.state === "done" ? "bg-primary text-primary-foreground" : "bg-foreground/10",
            )}
          >
            {set.state === "done" ? <Check aria-hidden className="size-4" strokeWidth={3} /> : set.number}
          </span>
          <span className="flex-1 font-semibold tabular-nums">
            {m.workout_values_weighted({ weight: kilos(set.weight), reps: set.reps })}
          </span>
          {set.state === "now" ? (
            <span className="rounded-full bg-primary px-2.5 py-1 text-[13px] font-semibold text-primary-foreground tabular-nums">
              {m.features_more_kg({ kg: kilos(2.5) })}
            </span>
          ) : null}
        </li>
      ))}
    </ul>
    <div className="mt-auto px-4 pb-[34px]">
      <p className="mb-3 text-center text-[14px] text-muted-foreground">
        {m.workout_up_next({ what: m.features_ex_row() })}
      </p>
      <span className="flex h-[60px] items-center justify-center rounded-full bg-primary text-[18px] font-semibold text-primary-foreground shadow-[0_10px_30px_-8px_var(--primary)]">
        {m.workout_log_set({ number: 3 })}
      </span>
    </div>
  </div>
);

/** How rested each muscle is after the last sessions: legs still need a day. */
const RECOVERY: Record<Muscle, number> = {
  [Muscle.CHEST]: 100,
  [Muscle.UPPER_BACK]: 84,
  [Muscle.LATS]: 82,
  [Muscle.LOWER_BACK]: 70,
  [Muscle.TRAPS]: 92,
  [Muscle.FRONT_DELTS]: 100,
  [Muscle.SIDE_DELTS]: 96,
  [Muscle.REAR_DELTS]: 88,
  [Muscle.BICEPS]: 90,
  [Muscle.TRICEPS]: 100,
  [Muscle.FOREARMS]: 94,
  [Muscle.ABS]: 100,
  [Muscle.OBLIQUES]: 96,
  [Muscle.GLUTES]: 58,
  [Muscle.QUADS]: 46,
  [Muscle.HAMSTRINGS]: 52,
  [Muscle.ADDUCTORS]: 64,
  [Muscle.ABDUCTORS]: 72,
  [Muscle.CALVES]: 78,
  [Muscle.NECK]: 100,
};

const GROUPS = [
  { id: "push", name: () => m.recovery_group_push(), value: 98, status: () => m.recovery_ready_now() },
  { id: "pull", name: () => m.recovery_group_pull(), value: 84, status: () => m.recovery_ready_now() },
  { id: "legs", name: () => m.recovery_group_legs(), value: 52, status: () => m.recovery_ready_in({ hours: 18 }) },
];

export const RecoveryScreen = () => (
  <div className={screen}>
    <LargeTitle kicker={m.features_recovery_kicker()} title={m.recovery_title()} />
    <MuscleMap
      toneOf={(muscle) => recoveryTone(RECOVERY[muscle])}
      titleOf={(muscle) => MUSCLE_LABELS[muscle]()}
      className="mt-5 h-[300px] px-8"
    />
    <ul className="mt-4 grid gap-2 px-4">
      {GROUPS.map((group) => (
        <li key={group.id} className={cn("flex h-[58px] items-center gap-3 rounded-[18px] px-4", well)}>
          <span className="flex-1">
            <b className="block text-[17px] leading-tight font-semibold">{group.name()}</b>
            <small className="text-[13px] text-muted-foreground">{group.status()}</small>
          </span>
          <b className={cn("text-[20px] font-semibold tabular-nums", recoveryTone(group.value))}>{group.value}%</b>
        </li>
      ))}
    </ul>
  </div>
);

/** The camera pointed at a plate, finding the foods on it. */
export const ScanScreen = () => (
  <div className="absolute inset-0">
    <MealScan className="absolute inset-0 rounded-none" />
    <div className="absolute inset-x-0 top-[62px] flex items-center justify-between px-4 text-white">
      <span className="grid size-[38px] place-items-center rounded-full bg-black/45 backdrop-blur-md">
        <ChevronLeft aria-hidden className="size-5" />
      </span>
      <span className="rounded-full bg-black/45 px-3.5 py-2 text-[14px] font-semibold backdrop-blur-md">
        {m.features_scan_title()}
      </span>
      <span className="size-[38px]" />
    </div>
  </div>
);

/** A Monday that is always a Monday, so the week strip has the same days on the server and in the browser. */
const MONDAY = Date.UTC(2026, 10, 2);
const weekDays = () =>
  Array.from({ length: 7 }, (_, index) => {
    const day = new Date(MONDAY + index * 86_400_000);
    return {
      key: index,
      short: new Intl.DateTimeFormat(getLocale(), { weekday: "short", timeZone: "UTC" }).format(day),
      number: day.getUTCDate(),
    };
  });

const SLOTS = ["07:00", "08:30", "12:00", "17:30", "18:30", "19:30"];

/** A coach's page in the app: the offer, the week and the free times, one tap from booked. */
export const BookingScreen = () => (
  <div className={screen}>
    <div className="flex items-center gap-3.5 px-5 pt-3">
      <Monogram name={m.features_sample_coach_full()} className="size-[58px] rounded-[20px] text-[19px]" />
      <span>
        <b className="block text-[22px] leading-tight font-semibold tracking-[-0.02em]">
          {m.features_sample_coach_full()}
        </b>
        <small className="text-[15px] text-muted-foreground">{m.features_sample_coach_role()}</small>
      </span>
    </div>
    <div className={cn("mx-4 mt-5 flex items-center justify-between rounded-[20px] px-4 py-3.5", well)}>
      <span>
        <b className="block text-[17px] font-semibold">{m.portal_sample_session()}</b>
        <small className="text-[14px] text-muted-foreground">{m.features_booking_length({ minutes: 60 })}</small>
      </span>
      <b className="text-[17px] font-semibold tabular-nums">{m.features_booking_price()}</b>
    </div>
    <div className="mt-5 grid grid-cols-7 gap-1.5 px-4">
      {weekDays().map((day) => (
        <span
          key={day.key}
          className={cn(
            "grid h-[64px] place-items-center rounded-[18px] py-2 text-center leading-none",
            day.key === 2 ? "bg-foreground text-background" : well,
            day.key > 5 && "text-muted-foreground",
          )}
        >
          <small className="text-[12px] font-medium capitalize opacity-70">{day.short}</small>
          <b className="text-[19px] font-semibold tabular-nums">{day.number}</b>
        </span>
      ))}
    </div>
    <div className="mt-4 grid grid-cols-3 gap-2 px-4">
      {SLOTS.map((slot) => (
        <span
          key={slot}
          className={cn(
            "grid h-[48px] place-items-center rounded-[16px] text-[16px] font-semibold tabular-nums",
            slot === "17:30" ? "bg-primary text-primary-foreground" : well,
            slot === "12:00" && "text-muted-foreground line-through opacity-50",
          )}
        >
          {slot}
        </span>
      ))}
    </div>
    <span className="mx-4 mt-5 flex h-[56px] items-center justify-center rounded-full bg-primary text-[17px] font-semibold text-primary-foreground">
      {m.features_booking_cta({ time: "17:30" })}
    </span>
    <p className="mt-3 flex items-center justify-center gap-1.5 text-[14px] text-muted-foreground">
      <Check aria-hidden className="size-4 text-primary" />
      {m.features_booking_policy()}
    </p>
    <div className={cn("mx-4 mt-5 flex items-center justify-between rounded-[20px] px-4 py-3.5", well)}>
      <span>
        <b className="block text-[17px] font-semibold">{m.features_booking_pack()}</b>
        <small className="text-[14px] text-muted-foreground">{m.features_booking_length({ minutes: 60 })}</small>
      </span>
      <b className="text-[17px] font-semibold tabular-nums">{m.features_booking_pack_price()}</b>
    </div>
  </div>
);

/** Eight weeks of the best squat set, rising. */
const BEST = [80, 80, 82.5, 85, 85, 87.5, 90, 92.5];

const progressLine = () => {
  const low = 76;
  const high = 95;
  const points = BEST.map((value, index) => [
    (index / (BEST.length - 1)) * 360,
    180 - ((value - low) / (high - low)) * 160,
  ]);
  const line = points
    .map(([x, y], index) => {
      if (index === 0) return `M${x} ${y}`;
      const [px, py] = points[index - 1] ?? [x, y];
      const mid = (px + x) / 2;
      return `C${mid} ${py} ${mid} ${y} ${x} ${y}`;
    })
    .join(" ");
  return { line, area: `${line} L360 200 L0 200 Z`, last: points[points.length - 1] ?? [360, 20] };
};

export const ProgressScreen = () => {
  const { line, area, last } = progressLine();
  return (
    <div className={screen}>
      <LargeTitle kicker={m.features_ex_squat()} title={m.features_progress_title()} />
      <div className="mt-4 flex gap-1.5 px-5">
        {[m.features_progress_tab_strength(), m.features_progress_tab_body(), m.features_progress_tab_diet()].map(
          (tab, index) => (
            <span
              key={tab}
              className={cn(
                "rounded-full px-3.5 py-1.5 text-[14px] font-semibold",
                index === 0 ? "bg-foreground text-background" : cn(well, "text-muted-foreground"),
              )}
            >
              {tab}
            </span>
          ),
        )}
      </div>
      <div className="px-5 pt-6">
        <p className="text-[15px] text-muted-foreground">{m.features_progress_best()}</p>
        <p className="mt-1 flex items-baseline gap-2.5">
          <b className="text-[48px] leading-none font-bold tracking-[-0.04em] tabular-nums">{kilos(92.5)} kg</b>
          <span className="rounded-full bg-primary/15 px-2.5 py-1 text-[14px] font-semibold text-primary">
            {m.features_more_kg({ kg: kilos(12.5) })}
          </span>
        </p>
      </div>
      <svg viewBox="-8 0 376 210" className="mt-5 w-full px-3">
        <defs>
          <linearGradient id="features-progress-fill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.45" />
            <stop offset="100%" stopColor="var(--primary)" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[40, 90, 140].map((y) => (
          <line key={y} x1="0" x2="360" y1={y} y2={y} className="stroke-foreground/10" strokeDasharray="3 5" />
        ))}
        <path d={area} fill="url(#features-progress-fill)" />
        <path d={line} className="fill-none stroke-primary" strokeWidth="3.5" strokeLinecap="round" />
        <circle cx={last[0]} cy={last[1]} r="11" className="fill-primary/25" />
        <circle cx={last[0]} cy={last[1]} r="6" className="fill-primary stroke-background" strokeWidth="2.5" />
      </svg>
      <div className="flex justify-between px-5 pt-1 text-[12px] text-muted-foreground tabular-nums">
        {[1, 2, 3, 4, 5, 6, 7, 8].map((week) => (
          <span key={week}>{m.features_progress_week({ n: week })}</span>
        ))}
      </div>
      <p className="mt-6 px-5 text-[13px] font-semibold tracking-[0.06em] text-muted-foreground uppercase">
        {m.features_progress_records()}
      </p>
      <ul className="mt-2 grid gap-2 px-4">
        {[
          { id: "deadlift", name: m.features_ex_deadlift(), weight: 140, gain: 5 },
          { id: "press", name: m.features_ex_press(), weight: 30, gain: 2 },
        ].map((record) => (
          <li key={record.id} className={cn("flex h-[56px] items-center gap-3 rounded-[18px] px-4", well)}>
            <span className="grid size-[30px] place-items-center rounded-full bg-primary/15 text-primary">
              <TrendingUp aria-hidden className="size-4" />
            </span>
            <b className="flex-1 text-[16px] font-semibold">{record.name}</b>
            <span className="text-[16px] font-semibold tabular-nums">{kilos(record.weight)} kg</span>
            <small className="text-[13px] font-semibold text-primary tabular-nums">
              {m.features_more_kg({ kg: kilos(record.gain) })}
            </small>
          </li>
        ))}
      </ul>
    </div>
  );
};

const LIVE = [
  { id: "squat", name: () => m.features_ex_squat(), sets: "4 × 8", weight: 82.5, done: 2 },
  { id: "row", name: () => m.features_ex_row(), sets: "3 × 10", weight: 60, done: 0 },
  { id: "press", name: () => m.features_ex_press(), sets: "3 × 10", weight: 26, done: 0, fresh: true },
];

/** A session with the coach, live: the plan fills in as the coach writes it. */
export const LiveScreen = () => (
  <div className={screen}>
    <div className="px-5 pt-3">
      <span className="inline-flex items-center gap-2 rounded-full bg-primary/15 px-3 py-1 text-[13px] font-semibold text-primary">
        <i className="size-2 animate-pulse rounded-full bg-primary motion-reduce:animate-none" />
        {m.coached_live_badge()}
      </span>
      <h3 className="mt-3 text-[30px] leading-[1.1] font-bold tracking-[-0.03em]">{m.coached_live_title()}</h3>
      <p className="mt-1 text-[15px] text-muted-foreground">
        {m.coached_live_coach({ name: m.features_sample_coach() })}
      </p>
    </div>
    <ul className="mt-5 grid gap-2 px-4">
      {LIVE.map((exercise) => (
        <li
          key={exercise.id}
          className={cn(
            "flex items-center gap-3 rounded-[20px] p-3",
            well,
            exercise.fresh && "animate-pulse ring-1 ring-primary/50 ring-inset motion-reduce:animate-none",
          )}
        >
          <span className="grid size-[46px] place-items-center rounded-[14px] bg-foreground/[0.08]">
            <Dumbbell aria-hidden className="size-5 text-muted-foreground" />
          </span>
          <span className="flex-1 leading-tight">
            <b className="block text-[16px] font-semibold">{exercise.name()}</b>
            <small className="text-[14px] text-muted-foreground tabular-nums">
              {exercise.sets} · {kilos(exercise.weight)} kg
            </small>
          </span>
          {exercise.done ? (
            <span className="text-[14px] font-semibold text-primary tabular-nums">
              {exercise.done}/{exercise.sets.split(" ")[0]}
            </span>
          ) : null}
        </li>
      ))}
      <li className="flex h-[52px] items-center justify-center gap-2 rounded-[20px] border border-dashed border-foreground/15 text-[14px] text-muted-foreground">
        <Plus aria-hidden className="size-4" />
        {m.features_live_writing({ name: m.features_sample_coach() })}
      </li>
    </ul>
  </div>
);

/** A Wednesday the lock screen always shows, so the server and the browser print the same date. */
const WEDNESDAY = Date.UTC(2026, 10, 4);

const notification = "rounded-[26px] bg-white/[0.14] p-4 text-white backdrop-blur-2xl";

/**
 * The lock screen the evening before a session: the morning's day summary by e-mail and the text
 * that reminds about tomorrow. Reminders arrive as e-mail and SMS, so that is what lands here.
 */
export const ReminderScreen = () => {
  const date = new Intl.DateTimeFormat(getLocale(), {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(WEDNESDAY);
  return (
    <div className="absolute inset-0 overflow-hidden bg-[oklch(0.16_0.01_130)] text-white">
      <span className="absolute -top-24 -left-16 size-[340px] rounded-full bg-primary/45 blur-[90px]" />
      <span className="absolute top-[260px] -right-24 size-[300px] rounded-full bg-[oklch(0.55_0.12_160)]/40 blur-[90px]" />
      <div className="relative pt-[78px] text-center">
        <p className="text-[19px] font-semibold opacity-85 first-letter:uppercase">{date}</p>
        <p className="mt-1 text-[96px] leading-none font-semibold tracking-[-0.04em] tabular-nums">20:14</p>
      </div>
      <div className="absolute inset-x-3 top-[330px] grid gap-2.5">
        <div className={notification}>
          <div className="flex items-center gap-2.5">
            <span className="grid size-[26px] place-items-center rounded-[7px] bg-[oklch(0.72_0.19_145)]">
              <MessageCircle aria-hidden className="size-4 fill-white text-white" />
            </span>
            <b className="flex-1 text-[15px] font-semibold">{m.portal_remind_sms_from()}</b>
            <small className="text-[13px] opacity-60">{m.how_lock_now()}</small>
          </div>
          <p className="mt-2 text-[16px] leading-snug">{m.portal_remind_sms_text()}</p>
          <p className="mt-1 text-[15px] opacity-60">{m.portal_step_remind_card_detail()}</p>
        </div>
        <div className={cn(notification, "bg-white/[0.1]")}>
          <div className="flex items-center gap-2.5">
            <span className="grid size-[26px] place-items-center rounded-[7px] bg-[oklch(0.62_0.17_250)]">
              <Mail aria-hidden className="size-4 text-white" />
            </span>
            <b className="flex-1 text-[15px] font-semibold">{m.portal_remind_sms_from()}</b>
            <small className="text-[13px] opacity-60">7:00</small>
          </div>
          <p className="mt-2 text-[16px] leading-snug font-semibold">
            {m.portal_remind_email_subject({ day: date })}
          </p>
          <p className="mt-1 text-[15px] leading-snug opacity-70">
            {m.portal_remind_digest_session()} · {m.portal_remind_digest_plan()}
          </p>
        </div>
      </div>
      <div className="absolute inset-x-0 bottom-[44px] flex justify-between px-12">
        {[
          { id: "flashlight", Icon: Flashlight },
          { id: "camera", Icon: Camera },
        ].map(({ id, Icon }) => (
          <span
            key={id}
            className="grid size-[50px] place-items-center rounded-full bg-white/[0.14] backdrop-blur-xl"
          >
            <Icon aria-hidden className="size-5" />
          </span>
        ))}
      </div>
    </div>
  );
};
